import React, { useEffect, useState } from 'react';
import OfferCard, { Freshness } from './OfferCard';
import { DECK } from './DeckReshuffle';

/* 3: Offer Card freshness cycle (looped for demo only) */
const FRESH_STATES: Freshness[] = ['fresh', 'aging', 'stale', 'syncing'];

export const FreshnessCycleLoop: React.FC = () => {
  const [i, setI] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setI(v => (v + 1) % FRESH_STATES.length), 1800);
    return () => clearInterval(id);
  }, []);
  const shell = DECK.find(d => d.id === 'shell')!;
  return (
    <OfferCard merchant={shell.merchant} logo={shell.logo} category="gas" value="10% back" freshness={FRESH_STATES[i]} expiresIn="6d left" />
  );
};

/* 4: Sync status pulse (syncing <-> synced, breathing) */
export const SyncPulseLoop: React.FC = () => {
  const [syncing, setSyncing] = useState(true);
  useEffect(() => {
    const id = setInterval(() => setSyncing(v => !v), 2200);
    return () => clearInterval(id);
  }, []);
  return (
    <div className={`cache__syncbadge cache__syncbadge--${syncing ? 'syncing' : 'synced'}`}>
      <span className="cache__syncbadge-dot" aria-hidden="true" />
      <span className="cache__syncbadge-icon" aria-hidden="true">{syncing ? '↻' : '✓'}</span>
      <span className="cache__syncbadge-label">{syncing ? 'Syncing…' : 'Synced'}</span>
    </div>
  );
};

/* 5: Add Card flow, sped up as a quick demo loop */
const ADD_CARD_STEPS = [
  { label: 'Choose issuer', chips: ['Amex', 'Chase', 'Citi'] },
  { label: 'Card type', chips: ['Platinum', 'Sapphire', 'Double Cash'] },
  { label: 'Confirm', chips: ['•••• 4821 added'] },
];

export const AddCardFlowLoop: React.FC = () => {
  const [step, setStep] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setStep(v => (v + 1) % ADD_CARD_STEPS.length), 1600);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="cache__flowloop">
      <div className="cache__flowloop-dots">
        {ADD_CARD_STEPS.map((_, i) => (
          <span key={i} className={`cache__flowloop-dot ${i === step ? 'is-active' : ''} ${i < step ? 'is-done' : ''}`} />
        ))}
      </div>
      <div className="cache__flowloop-label">{ADD_CARD_STEPS[step].label}</div>
      <div className="cache__flowloop-options">
        {ADD_CARD_STEPS[step].chips.map(c => (
          <div key={c} className="cache__flowloop-chip">{c}</div>
        ))}
      </div>
    </div>
  );
};

/* 6: Onboarding "deal yourself a hand" */
const CATEGORIES: { key: string; label: string }[] = [
  { key: 'dining', label: 'dining' },
  { key: 'travel', label: 'travel' },
  { key: 'groceries', label: 'groceries' },
  { key: 'gas', label: 'gas' },
  { key: 'entertainment', label: 'entertainment' },
  { key: 'retail', label: 'retail' },
];

export const DealHandLoop: React.FC = () => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    let i = 0;
    const id = setInterval(() => {
      i = (i + 1) % (CATEGORIES.length + 2);
      setCount(i > CATEGORIES.length ? 0 : i);
    }, 550);
    return () => clearInterval(id);
  }, []);
  return (
    <div className="cache__dealhand">
      {CATEGORIES.map((c, i) => (
        <div
          key={c.key}
          className={`cache__dealhand-chip cache__deck-tag--${c.key} ${i < count ? 'is-selected' : ''}`}
          style={{ '--i': i - (CATEGORIES.length - 1) / 2 } as React.CSSProperties}
        >
          {c.label}
        </div>
      ))}
    </div>
  );
};

/* 7: Save/dismiss swipe */
export const SwipeLoop: React.FC = () => {
  const [phase, setPhase] = useState<'idle' | 'left' | 'right'>('idle');
  useEffect(() => {
    const seq: Array<'left' | 'idle' | 'right'> = ['left', 'idle', 'right', 'idle'];
    let i = 0;
    const id = setInterval(() => { setPhase(seq[i % seq.length]); i++; }, 1300);
    return () => clearInterval(id);
  }, []);
  const netflix = DECK.find(d => d.id === 'netflix')!;
  return (
    <div className="cache__swipeloop">
      <span className="cache__swipeloop-hint cache__swipeloop-hint--left">dismiss</span>
      <div className={`cache__swipeloop-card cache__swipeloop-card--${phase}`}>
        <div className="cache__deck-brand">
          <img className="cache__deck-logo" src={netflix.logo} alt="" aria-hidden="true" />
          <div className="cache__deck-merchant">{netflix.merchant}</div>
        </div>
        <div className="cache__deck-value">{netflix.value}</div>
      </div>
      <span className="cache__swipeloop-hint cache__swipeloop-hint--right">save</span>
    </div>
  );
};

/* 2 (grid variant): Quick Check deal, auto-cycling queries */
const QUERIES = ['Starbucks', 'Delta', 'Shell'];

export const QuickCheckAutoLoop: React.FC = () => {
  const [i, setI] = useState(0);
  const [dealt, setDealt] = useState(false);
  useEffect(() => {
    setDealt(false);
    const t1 = setTimeout(() => setDealt(true), 300);
    const t2 = setTimeout(() => setI(v => (v + 1) % QUERIES.length), 2400);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [i]);
  const card = DECK.find(d => d.merchant === QUERIES[i]);
  return (
    <div className="cache__qcloop">
      <div className="cache__qcloop-query">“{QUERIES[i]}”</div>
      {card && (
        <div className={`cache__qc-card ${dealt ? 'is-dealt' : ''}`}>
          <div className="cache__offercard-top">
            <span className={`cache__deck-tag cache__deck-tag--${card.category}`}>{card.category}</span>
          </div>
          <img className="cache__deck-logo" src={card.logo} alt={card.merchant} />
          <div className="cache__deck-value">{card.value}</div>
        </div>
      )}
    </div>
  );
};
